import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Globe, Loader2, ExternalLink, ShoppingBag, Plus, Sparkles, Link2, Copy, CheckCircle2, RefreshCw } from "lucide-react";
import { API, formatApiError, useAuth } from "@/contexts/AuthContext";

const STATUS_META = {
  dns_verified: {
    label: "DNS detected · Publication pending",
    tone: "bg-sky-50 text-sky-700 border-sky-200",
    title: "Domain ownership and DNS routing are verified. Website publication and SSL still require separate confirmation.",
  },
  // Stripe payment confirmation is NOT proof of registrar registration.
  // Only a verified registrar response may be displayed as "Registered".
  paid: {
    label: "Payment received · Registration pending",
    tone: "bg-amber-50 text-amber-700 border-amber-200",
    title: "Payment was recorded, but domain registration at the registrar has not yet been confirmed. Please do not purchase the domain again while this is being checked.",
  },
  pending_dns: {
    label: "DNS pending",
    tone: "bg-sky-50 text-sky-700 border-sky-200",
    title: "DNS verification is pending. This status does not confirm registration, payment or website publication.",
  },
  pending_payment: {
    label: "Pending payment",
    tone: "bg-amber-50 text-amber-700 border-amber-200",
    title: "Checkout started but payment not yet confirmed by Stripe.",
  },
  pending_manual: {
    label: "Pending",
    tone: "bg-slate-100 text-slate-700 border-slate-200",
    title: "Awaiting manual registration provisioning.",
  },
  failed: {
    label: "Failed",
    tone: "bg-rose-50 text-rose-700 border-rose-200",
    title: "Registration failed — please contact support.",
  },
};

const SIX_ZYNTHORO_DOMAINS = [
  "zynthoro.es", "zynthoro.site", "zynthoro.store",
  "zynthoro.online", "zynthoro.shop", "zynthoro.space",
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return "—";
  }
}

function fmtPrice(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return `€${n.toFixed(2).replace(/\.00$/, "")}`;
}

function CopyBtn({ value }) {
  const [ok, setOk] = useState(false);
  const [failed, setFailed] = useState(false);
  const [copying, setCopying] = useState(false);
  useEffect(() => {
    setOk(false); setFailed(false);
  }, [value]);
  useEffect(() => {
    if (!ok) return undefined;
    const timer = setTimeout(() => setOk(false), 1400);
    return () => clearTimeout(timer);
  }, [ok]);
  const copy = async () => {
    if (copying) return;
    setCopying(true); setOk(false); setFailed(false);
    try {
      await navigator.clipboard.writeText(String(value));
      setOk(true);
    } catch {
      setFailed(true);
    } finally { setCopying(false); }
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <button
        type="button"
        onClick={copy}
        disabled={copying}
        aria-label="Copy DNS value"
        data-testid={`copy-${value}`}
        className="inline-flex items-center gap-1 text-slate-500 hover:text-[var(--zy-blue,#202B60)] text-[11.5px] disabled:opacity-50"
      >
        {ok ? <CheckCircle2 size={12} className="text-emerald-600" /> : <Copy size={12} />}
        {copying ? "Copying…" : ok ? "Copied" : "Copy"}
      </button>
      <span role="status" className="text-[11.5px]">
        {failed ? "Copy unavailable. Select the DNS value and copy it manually." : ok ? "DNS value copied." : ""}
      </span>
    </span>
  );
}

function DnsRecords({ records, verifiedApex, verifiedWww, verifiedOwnership }) {
  if (!records) return null;
  const rows = [
    { key: "ownership", rec: records.ownership, ok: verifiedOwnership },
    { key: "apex", rec: records.apex, ok: verifiedApex },
    { key: "www",  rec: records.www,  ok: verifiedWww },
  ].filter((r) => r.rec && typeof r.rec === "object");
  if (!rows.length) return null;
  return (
    <div role="region" aria-label="DNS records — scroll horizontally on smaller screens" tabIndex={0}
      className="mt-3 max-w-full overflow-x-auto rounded-lg border border-slate-200 bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-500">
      <table className="w-full min-w-[680px] text-[12.5px]">
        <thead className="bg-slate-50/70 text-slate-500 text-[11px] uppercase tracking-wider">
          <tr>
            <th scope="col" className="text-left font-medium px-3 py-1.5">Type</th>
            <th scope="col" className="text-left font-medium px-3 py-1.5">Host</th>
            <th scope="col" className="text-left font-medium px-3 py-1.5">Value</th>
            <th scope="col" className="text-left font-medium px-3 py-1.5">TTL</th>
            <th scope="col" className="text-right font-medium px-3 py-1.5">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map(({ key, rec, ok }) => (
            <tr key={key} data-testid={`byod-record-${key}`}>
              <td className="px-3 py-2 font-mono text-slate-800">{rec.type}</td>
              <td className="px-3 py-2 font-mono text-slate-800">{rec.host}</td>
              <td className="min-w-[240px] max-w-[440px] px-3 py-2 font-mono text-slate-900">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 select-all break-all">{rec.value}</span>
                  <CopyBtn value={rec.value} />
                </div>
              </td>
              <td className="px-3 py-2 text-slate-600">{rec.ttl}s</td>
              <td className="px-3 py-2 text-right">
                {ok === true && <span className="text-emerald-700 text-[11.5px] font-semibold">✓ Detected</span>}
                {ok === false && <span className="text-slate-400 text-[11.5px]">Not yet</span>}
                {ok === undefined && <span className="text-slate-400 text-[11.5px]">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConnectOwnDomain({ onChanged }) {
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [session, setSession] = useState(null); // { domain, records, verified?, observed? }

  const connect = async () => {
    if (busy) return;
    const d = input.trim().toLowerCase();
    if (!d) { setErr("Enter a domain you already own, like mycompany.com."); return; }
    setBusy(true); setErr(null);
    try {
      const { data } = await axios.post(`${API}/website-domains/byod/connect`, { domain: d });
      setSession({
        domain: data.domain,
        records: data.records,
        already_connected: data.already_connected,
      });
      setInput("");
      if (onChanged) onChanged();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e?.message || "Could not connect that domain.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (busy || !session?.domain?.domain) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await axios.post(`${API}/website-domains/byod/verify`, {
        domain: session.domain.domain,
      });
      setSession((s) => ({
        ...s,
        domain: data.domain,
        records: data.records,
        verified: data.verified,
        observed: data.observed,
      }));
      if (onChanged) onChanged();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e?.message || "Verification failed.");
    } finally {
      setBusy(false);
    }
  };

  const lastDnsResult = session?.domain?.dns_last_result;
  const observed = session?.observed || lastDnsResult?.observed;
  const verifiedOwnership = observed
    ? !!session?.records?.ownership?.value &&
      (observed.ownership_txt || []).includes(session.records.ownership.value)
    : undefined;
  const verifiedApex = observed
    ? (observed.apex_a || []).map((value) => value.toLowerCase())
        .includes(String(session?.records?.apex?.value || "").toLowerCase())
    : undefined;
  const verifiedWww = observed
    ? ((observed.www_cname || []).map((value) => value.toLowerCase())
        .includes(String(session?.records?.www?.value || "").toLowerCase())
       || (observed.www_a || []).map((value) => value.toLowerCase())
        .includes(String(session?.records?.apex?.value || "").toLowerCase()))
    : undefined;
  const dnsVerified = verifiedOwnership === true &&
    (verifiedApex === true || verifiedWww === true);

  return (
    <div
      className="mb-5 min-w-0 max-w-full rounded-xl border border-sky-200 bg-sky-50/60 p-4"
      data-testid="byod-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[13px] font-semibold text-sky-900 flex items-center gap-1.5">
            <Link2 size={13} />
            Connect your own domain
          </p>
          <p className="text-[12px] text-sky-900/80 mt-0.5">
            Already own a domain? Point it at Zynthoro — no new purchase.
            We'll show the DNS records to configure. Website publication is a separate step after verification.
          </p>
        </div>
      </div>

      {!session && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="mycompany.com"
            disabled={busy}
            data-testid="byod-input"
            className="flex-1 min-w-[220px] rounded-md border border-sky-300 bg-white px-3 py-2 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-300"
            onKeyDown={(e) => { if (e.key === "Enter") connect(); }}
          />
          <button
            type="button"
            onClick={connect}
            disabled={busy || !input.trim()}
            data-testid="byod-connect-submit"
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--zy-blue,#202B60)] text-white px-3.5 py-2 text-[13px] font-semibold hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
            Connect
          </button>
        </div>
      )}

      {session && (
        <div className="mt-3" data-testid="byod-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-[13px] text-slate-900">
              <span className="font-semibold font-mono">{session.domain.domain}</span>{" "}
              {dnsVerified ? (
                <span className="ml-1 text-emerald-700 font-semibold">— DNS detected</span>
              ) : (
                <span className="ml-1 text-sky-700 font-semibold">— DNS pending</span>
              )}
              {session.already_connected && (
                <span className="ml-2 text-[11.5px] text-slate-500">
                  (already connected to your account)
                </span>
              )}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={verify}
                disabled={busy}
                data-testid="byod-verify-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-sky-300 bg-white text-sky-900 px-3 py-1.5 text-[12.5px] font-semibold hover:bg-sky-100 disabled:opacity-60"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                {dnsVerified ? "Re-check DNS" : "Verify DNS"}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => { setSession(null); setErr(null); }}
                data-testid="byod-close-panel"
                className="text-[12px] text-slate-500 hover:text-slate-800"
              >
                Add another
              </button>
            </div>
          </div>
          <p className="text-[12px] text-slate-600 mt-2">
            In your DNS provider, add the TXT ownership record and the routing records shown below. Propagation time depends on your provider and DNS cache.
            Detected DNS records do not confirm website publication or an active SSL certificate.
          </p>
          <DnsRecords
            records={session.records}
            verifiedApex={verifiedApex}
            verifiedWww={verifiedWww}
            verifiedOwnership={verifiedOwnership}
          />
          {!dnsVerified && session.verified === false && (
            <p className="text-[11.5px] text-slate-500 mt-2" data-testid="byod-verify-not-yet">
              Ownership and DNS routing are not both confirmed yet. Check all records shown above, then try "Verify DNS" again.
            </p>
          )}
        </div>
      )}

      {err && (
        <p className="mt-2 text-[12px] text-rose-700" data-testid="byod-err">
          {err}
        </p>
      )}
    </div>
  );
}

function FounderAttach({ onAttached }) {
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const run = useCallback(async (payload, label) => {
    setBusy(true); setMsg(null); setErr(null);
    try {
      const { data } = await axios.post(`${API}/founder/domains/attach`, payload);
      const attached = data?.attached_count ?? 0;
      const notFound = data?.not_found_count ?? 0;
      setMsg(
        `${label}: attached ${attached}${notFound ? ` · not found ${notFound}` : ""}`
      );
      if (onAttached) onAttached();
      if (payload.domain) setInput("");
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e?.message || "Attach failed.");
    } finally {
      setBusy(false);
    }
  }, [onAttached]);

  const attachOne = () => {
    const d = input.trim().toLowerCase();
    if (!d) { setErr("Enter a domain name."); return; }
    run({ domain: d }, d);
  };

  const attachSix = () => run({ prefill_six: true }, "Six Zynthoro domains");

  return (
    <div
      className="mb-5 rounded-xl border border-amber-200 bg-amber-50/60 p-4"
      data-testid="founder-attach-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[13px] font-semibold text-amber-900 flex items-center gap-1.5">
            <Sparkles size={13} />
            Founder tools · Attach paid domain
          </p>
          <p className="text-[12px] text-amber-900/80 mt-0.5">
            Looks up the FQDN in domain orders or Stripe line items and
            transfers it to your founder account. No refund. Idempotent.
          </p>
        </div>
        <button
          type="button"
          onClick={attachSix}
          disabled={busy}
          data-testid="founder-attach-six-btn"
          className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white text-amber-900 px-3 py-1.5 text-[12.5px] font-semibold hover:bg-amber-100 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          Pre-fill the six (zynthoro.es/.site/.store/.online/.shop/.space)
        </button>
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="foo.com"
          disabled={busy}
          data-testid="founder-attach-input"
          className="flex-1 min-w-[220px] rounded-md border border-amber-300 bg-white px-3 py-2 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-300"
          onKeyDown={(e) => { if (e.key === "Enter") attachOne(); }}
        />
        <button
          type="button"
          onClick={attachOne}
          disabled={busy || !input.trim()}
          data-testid="founder-attach-submit"
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--zy-blue,#202B60)] text-white px-3.5 py-2 text-[13px] font-semibold hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          Attach
        </button>
      </div>

      {msg && (
        <p className="mt-2 text-[12px] text-emerald-800" data-testid="founder-attach-msg">
          {msg}
        </p>
      )}
      {err && (
        <p className="mt-2 text-[12px] text-rose-700" data-testid="founder-attach-err">
          {err}
        </p>
      )}
    </div>
  );
}

export default function DomainsPage() {
  const { user } = useAuth();
  const isFounder = !!user?.is_founder;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [domains, setDomains] = useState([]);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/website-domains/mine`);
        if (!cancelled) {
          setDomains(Array.isArray(data?.domains) ? data.domains : []);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            formatApiError(e?.response?.data?.detail) || e?.message || "Could not load your domains."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadTick]);

  const rows = useMemo(() => {
    return [...domains].sort((a, b) => {
      const aPaid = (a.status === "paid") ? 0 : 1;
      const bPaid = (b.status === "paid") ? 0 : 1;
      if (aPaid !== bPaid) return aPaid - bPaid;
      // Keep the six pinned by alphabetical FQDN when statuses match,
      // so the founder always sees a stable order.
      return String(a.domain || "").localeCompare(String(b.domain || ""));
    });
  }, [domains]);

  const onAttached = useCallback(() => setReloadTick((t) => t + 1), []);

  return (
    <div className="max-w-5xl mx-auto" data-testid="domains-page">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 flex items-center gap-2.5">
            <Globe size={22} className="text-[var(--zy-blue,#202B60)]" />
            My domains
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            All domains registered under your Zynthoro account.
          </p>
        </div>
        <Link
          to="/website"
          data-testid="domains-buy-cta"
          className="inline-flex items-center gap-1.5 rounded-full bg-[var(--zy-blue,#202B60)] text-white px-4 py-2 text-[13px] font-semibold hover:opacity-95 transition-opacity"
        >
          <ShoppingBag size={14} />
          Buy a domain
        </Link>
      </header>

      {isFounder && <FounderAttach onAttached={onAttached} />}
      <ConnectOwnDomain onChanged={onAttached} />

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="py-16 flex items-center justify-center text-slate-500" data-testid="domains-loading">
            <Loader2 size={18} className="animate-spin mr-2" />
            Loading your domains…
          </div>
        ) : error ? (
          <div className="py-10 px-6 text-center" data-testid="domains-error">
            <p className="text-rose-600 text-sm">{error}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 px-6 text-center" data-testid="domains-empty">
            <Globe size={28} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-700 font-medium">No domains yet</p>
            <p className="text-slate-500 text-sm mt-1">
              Buy one on the{" "}
              <Link to="/website" className="text-[var(--zy-blue,#202B60)] font-medium hover:underline">
                Website &amp; Domains page
              </Link>
              .
            </p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="domains-table">
            <thead className="bg-slate-50/70 text-slate-500 text-[12px] uppercase tracking-wider">
              <tr>
                <th className="text-left font-medium px-5 py-3">Domain</th>
                <th className="text-left font-medium px-5 py-3">Status</th>
                <th className="text-left font-medium px-5 py-3">Purchased on</th>
                <th className="text-right font-medium px-5 py-3">Price</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((d) => {
                const displayStatus = d.kind === "byod"
                  ? (d.dns_last_result?.verified === true && d.dns_last_result?.ownership_ok === true ? "dns_verified" : "pending_dns")
                  : d.status;
                const meta = STATUS_META[displayStatus] || STATUS_META.pending_manual;
                return (
                  <tr key={d.id} data-testid={`domain-row-${d.domain}`}>
                    <td className="px-5 py-3.5 text-slate-900 font-medium">
                      <span className="inline-flex items-center gap-2">
                        {d.domain}
                        {d.status === "paid" && (
                          <a
                            href={`https://${d.domain}`}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="text-slate-400 hover:text-[var(--zy-blue,#202B60)]"
                            aria-label={`Open ${d.domain}`}
                          >
                            <ExternalLink size={13} />
                          </a>
                        )}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[12px] font-medium ${meta.tone}`}
                        data-testid={`domain-status-${d.domain}`}
                        title={meta.title || meta.label}
                      >
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-600">
                      {/* Regression repair 2026-10-03 · Issue 9: prefer
                         `created_at` (never mutated after order creation)
                         over `paid_at` which the self-heal path has
                         historically stamped with the self-heal moment
                         rather than the real Stripe payment time. */}
                      {fmtDate(d.created_at || d.paid_at)}
                    </td>
                    <td className="px-5 py-3.5 text-right text-slate-900 font-medium">
                      {fmtPrice(d.price_eur)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-[12px] text-slate-500 mt-4">
        Domain purchases usually appear here within a minute of payment. If a domain you paid for
        is missing after that, refresh this page — pending orders self-heal from Stripe.
      </p>
    </div>
  );
}

// Expose SIX_ZYNTHORO_DOMAINS for tests / debugging.
export { SIX_ZYNTHORO_DOMAINS };
